import {
  Box,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import type { AlertSeverity, AlertStatus, AlertType, RiskFilters as RiskFiltersType } from "../types";
import type { SelectChangeEvent } from "@mui/material";

interface RiskFiltersProps {
  filters: RiskFiltersType;
  onFiltersChange: (filters: RiskFiltersType) => void;
  totalCount: number;
  filteredCount: number;
}

const severityOptions: { value: AlertSeverity; label: string }[] = [
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

const statusOptions: { value: AlertStatus; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "acknowledged", label: "Acknowledged" },
  { value: "investigating", label: "Investigating" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

const typeOptions: { value: AlertType; label: string }[] = [
  { value: "leak", label: "Leak" },
  { value: "pressure", label: "Pressure" },
  { value: "intrusion", label: "Intrusion" },
  { value: "equipment", label: "Equipment" },
  { value: "environmental", label: "Environmental" },
];

export const RiskFilters = ({ filters, onFiltersChange, totalCount, filteredCount }: RiskFiltersProps) => {
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ ...filters, search: event.target.value });
  };

  const handleSeverityChange = (event: SelectChangeEvent<AlertSeverity[]>) => {
    const value = event.target.value;
    onFiltersChange({ ...filters, severities: typeof value === "string" ? value.split(",") as AlertSeverity[] : value });
  };

  const handleStatusChange = (event: SelectChangeEvent<AlertStatus[]>) => {
    const value = event.target.value;
    onFiltersChange({ ...filters, statuses: typeof value === "string" ? value.split(",") as AlertStatus[] : value });
  };

  const handleTypeChange = (event: SelectChangeEvent<AlertType[]>) => {
    const value = event.target.value;
    onFiltersChange({ ...filters, types: typeof value === "string" ? value.split(",") as AlertType[] : value });
  };

  const clearFilters = () => {
    onFiltersChange({
      search: "",
      types: [],
      severities: [],
      statuses: [],
      dateRange: { start: null, end: null },
    });
  };

  const hasActiveFilters = filters.search || filters.types.length > 0 || filters.severities.length > 0 || filters.statuses.length > 0;

  const renderValue = (selected: string[], options: { value: string; label: string }[]) => {
    if (selected.length === 0) return "";
    if (selected.length === 1) {
      return options.find(o => o.value === selected[0])?.label || selected[0];
    }
    return `${selected.length} selected`;
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            placeholder="Search alerts..."
            size="small"
            value={filters.search}
            onChange={handleSearchChange}
            sx={{ minWidth: 200, flex: "0 1 300px" }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              },
            }}
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Severity</InputLabel>
            <Select
              multiple
              value={filters.severities}
              onChange={handleSeverityChange}
              input={<OutlinedInput label="Severity" />}
              renderValue={(selected) => renderValue(selected, severityOptions)}
            >
              {severityOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={filters.severities.includes(option.value)} size="small" />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={filters.statuses}
              onChange={handleStatusChange}
              input={<OutlinedInput label="Status" />}
              renderValue={(selected) => renderValue(selected, statusOptions)}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={filters.statuses.includes(option.value)} size="small" />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              multiple
              value={filters.types}
              onChange={handleTypeChange}
              input={<OutlinedInput label="Type" />}
              renderValue={(selected) => renderValue(selected, typeOptions)}
            >
              {typeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={filters.types.includes(option.value)} size="small" />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {hasActiveFilters && (
            <Chip
              label="Clear"
              size="small"
              onDelete={clearFilters}
              deleteIcon={<CloseIcon />}
              variant="outlined"
            />
          )}

          <Box sx={{ flex: 1 }} />

          <Typography variant="body2" color="text.secondary">
            {filteredCount} of {totalCount}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};
